import React, { useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import { PluginBlock } from 'ts-components/Plugins/PluginBlock';
import { PluginContext } from 'ts-components/Plugins/context';

const Wrapper = styled.div<{ height?: number }>`
  position: relative;
  width: 100%;
  height: ${({ height }) => (height ? `${height}px` : '300px')};
  overflow: hidden;
`;

interface PluginBlockRecordProps {
  metadata: {
    pluginApiName: string;
    blockApiName: string;
    recommendedHeight?: number;
  };
  objectId: string;
  entityId: string;
  blockId: string;
  savedAt?: number;
}

export const PluginBlockRecord = ({
  metadata,
  objectId,
  entityId,
  blockId,
  savedAt,
}: PluginBlockRecordProps) => {
  const { pluginApiName, blockApiName, recommendedHeight } = metadata ?? {};

  const {
    pluginBlocks,
    isLoading: pluginContextLoading,
    isFetched: pluginContextFetched,
  } = useContext(PluginContext);

  const isHydrated = !pluginContextLoading && pluginContextFetched;

  const block = useMemo(
    () =>
      pluginBlocks
        .find((entry) => entry.plugin_api_name === pluginApiName)
        ?.blocks.find((b: any) => b.api_name === blockApiName),
    [pluginBlocks, pluginApiName, blockApiName]
  );

  const args = useMemo(() => ({ objectId, entityId }), [objectId, entityId]);
  const instanceId = savedAt ? `${blockId}-${savedAt}` : blockId;

  // Once the plugin context has loaded, hide the entire block (including its
  // reserved space) if the plugin or block is not available / disabled.
  if (isHydrated && pluginApiName && blockApiName && !block) {
    return null;
  }

  return (
    <Wrapper height={recommendedHeight ?? 300}>
      <PluginBlock
        key={instanceId}
        pluginApiName={pluginApiName}
        blockApiName={blockApiName}
        instanceId={instanceId}
        args={args}
        surfaceFilter="records"
      />
    </Wrapper>
  );
};
